import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import cn from 'classnames'
import {
  ArrowForwardIcon,
  ChatBubbleInstaIcon,
  HeartIcon,
  MoreHorizIcon,
  SendIcon,
  BookmarkOutlineIcon,
} from 'images'
import { LazyLoadImage } from 'react-lazy-load-image-component'
import { useTranslation } from 'react-i18next'

export function InstagramPreview({ data, onLoaded, placement, label }) {
  const bodyRef = useRef(null)
  const [isLongText, setIsLongText] = useState(false)
  const [showMore, setShowMore] = useState(false)
  const { t } = useTranslation()

  useEffect(() => {
    if (bodyRef.current) setIsLongText(bodyRef.current.scrollHeight > bodyRef.current.clientHeight)
  }, [])

  if (!data) return null

  const { page_name, profile_picture_url, body, media, call_to_action, fb_info_loading } = data
  let aspectClass = 'ratio-4x5'
  if (media && media.width && media.height) {
    const ratio = media.width / media.height
    if (Math.abs(ratio - 1) < 0.13) {
      aspectClass = 'ratio-1x1'
    } else if (ratio > 1.4) {
      aspectClass = 'ratio-16x9'
    } else if (Math.abs(ratio - 0.8) < 0.13) {
      aspectClass = 'ratio-4x5'
    }
  }

  return (
    <div className="feature-card-container">
      <div className="feature-card">
        <div className="placement-ig-feed">
          <div className="placement-ig-feed__header d-flex justify-content-between gap-2 px-3">
            <div className="d-flex align-items-center flex-row w-100">
              <div>
                {profile_picture_url ? (
                  <img
                    alt={page_name}
                    className="placement-ig-feed__profile_picture"
                    src={profile_picture_url}
                    onError={(e) => {
                      e.target.style.display = 'none'
                      e.target.nextSibling.style.display = 'inline-block'
                    }}
                  />
                ) : null}
                <div className={cn('placement-ig-feed__icon', { loading: fb_info_loading })} style={{ display: profile_picture_url ? 'none' : 'inline-block' }} />
              </div>
              {page_name ? (
                <p className="placement-ig-feed__title">
                  <span>{page_name}</span>
                  <span className="subtitle">Sponsored</span>
                </p>
              ) : fb_info_loading ? (
                <div className="fb-error loading" />
              ) : (
                <div className="fb-error">Page Info</div>
              )}
            </div>
            <div className="placement-ig-feed__actions">
              <MoreHorizIcon width={30} />
            </div>
          </div>
          <div>
            <div className="placement-ig-feed__link">
              {media.type && media.type.includes('video') ? (
                <video
                  className={`placement-ig-feed__link_media ${aspectClass}`}
                  src={media.url}
                  autoPlay
                  loop
                  muted
                  playsInline
                  onLoad={onLoaded}
                />
              ) : (
                <LazyLoadImage
                  className={`placement-ig-feed__link_media ${aspectClass}`}
                  src={media.url}
                  alt={media.filename}
                  width={media.width}
                  height={media.height}
                  onLoad={onLoaded}
                />
              )}
              <div className="placement-ig-feed__link_order-now">
                <span>{t(`Recommendations.campaign.setup.call_to_action.${call_to_action}`)}</span>
                <span>
                  <ArrowForwardIcon />
                </span>
              </div>
            </div>
            <div className="divider" />
            <div className="placement-ig-feed__interactions">
              <div className="d-flex">
                <span>
                  <HeartIcon />
                </span>
                <span>
                  <ChatBubbleInstaIcon />
                </span>
                <span>
                  <SendIcon />
                </span>
              </div>
              <BookmarkOutlineIcon height={25} />
            </div>
          </div>
          <p className="placement-ig-feed__caption">
            <span ref={bodyRef} className={showMore ? 'text' : 'text_clipped'}>
              {body}
            </span>
            {isLongText && (
              <span className="more" onClick={() => setShowMore((v) => !v)}>
                {showMore ? 'less' : 'more'}
              </span>
            )}
          </p>
        </div>
      </div>
      <div className="feature-card-label">
        <span>{placement}</span>
        <span>{label}</span>
      </div>
    </div>
  )
}

InstagramPreview.propTypes = {
  data: PropTypes.object,
  onLoaded: PropTypes.func,
}

export default InstagramPreview
